import { create } from 'zustand';
import type {
  EventsViewTab,
  EventType,
  ScheduledEvent,
  EventMember,
  EventInvitee,
  EventContact,
  PrePopulateConfig,
} from '@/types/events';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface EventsState {
  /** Current sub-tab inside the Events view */
  activeTab: EventsViewTab;

  /** The authenticated user / "me" from the event provider */
  me: EventMember | null;

  /** Organisation members / calendars */
  members: EventMember[];

  /** Currently selected member/agent (null = logged-in user) */
  selectedAgent: EventMember | null;

  /** Whether "All Agents" view is toggled on */
  allAgentsSelected: boolean;

  /** Active (bookable) event types for the selected agent */
  eventTypes: EventType[];

  /** All org-level event types (for incoming config) */
  orgEventTypes: EventType[];

  /** Upcoming scheduled events */
  upcomingEvents: ScheduledEvent[];

  /** Past scheduled events */
  pastEvents: ScheduledEvent[];

  /** Invitees for the currently expanded event */
  currentEventInvitees: EventInvitee[];

  /** The currently expanded event (for detail modal) */
  expandedEvent: ScheduledEvent | null;

  /** Contact info from CRM (used for pre-population) */
  contact: EventContact | null;

  /** Contact email used for event history search */
  searchEmail: string;

  /** Contact phone filter (Acuity) */
  searchPhone: string;

  /** Calendar ID filter (Acuity) */
  searchCalendarId: string;

  /** Appointment type filter (Acuity) — '' = all types */
  searchAppointmentTypeId: string;

  /** Event status filter (Calendly) — '' = all statuses */
  searchStatus: '' | 'active' | 'canceled';

  /** Start-date range filter (Calendly) — 'YYYY-MM-DD', '' = no limit */
  searchFromDate: string;
  searchToDate: string;

  /** Pre-populate configs keyed by agentId → eventTypeId */
  prePopulateConfigs: Record<string, Record<string, PrePopulateConfig>>;

  /** Loading flags */
  loading: boolean;
  membersLoading: boolean;
  eventTypesLoading: boolean;
  eventsLoading: boolean;
  /** Error from the last scheduled-events fetch ('' = none) */
  eventsError: string;
  inviteesLoading: boolean;
  prePopulateConfigsLoading: boolean;

  /** Error state */
  error: string | null;

  /** Whether the store has been initialised at least once */
  initialized: boolean;

  /** Last visited event type ID (for visual indicator) */
  lastVisitedEventTypeId: string | null;

  /** When event types were last force-refreshed from the API (null = list may be cached) */
  eventTypesRefreshedAt: number | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

interface EventsActions {
  // Tab navigation
  setActiveTab(tab: EventsViewTab): void;

  // Me
  setMe(me: EventMember): void;

  // Members
  setMembers(members: EventMember[]): void;

  // Agent selection
  selectAgent(agent: EventMember | null): void;
  toggleAllAgents(): void;

  // Event types
  setEventTypes(types: EventType[]): void;
  setOrgEventTypes(types: EventType[]): void;

  // Scheduled events
  setScheduledEvents(events: ScheduledEvent[]): void;
  clearScheduledEvents(): void;

  // Event detail
  setExpandedEvent(event: ScheduledEvent | null): void;
  setCurrentEventInvitees(invitees: EventInvitee[]): void;

  // Contact / search filters
  setContact(contact: EventContact | null): void;
  setSearchEmail(email: string): void;
  setSearchPhone(phone: string): void;
  setSearchCalendarId(calendarId: string): void;
  setSearchAppointmentTypeId(appointmentTypeId: string): void;
  setSearchStatus(status: '' | 'active' | 'canceled'): void;
  setSearchDateRange(fromDate: string, toDate: string): void;

  // Last visited
  setLastVisitedEventType(eventTypeId: string): void;

  // Pre-populate
  setPrePopulateConfig(
    agentId: string,
    eventTypeId: string,
    config: PrePopulateConfig
  ): void;
  setPrePopulateConfigs(
    configs: Record<string, Record<string, PrePopulateConfig>>
  ): void;

  // Loading & error
  setLoading(loading: boolean): void;
  setMembersLoading(loading: boolean): void;
  setEventTypesLoading(loading: boolean): void;
  setEventTypesRefreshedAt(time: number | null): void;
  setEventsLoading(loading: boolean): void;
  setEventsError(error: string): void;
  setInviteesLoading(loading: boolean): void;
  setPrePopulateConfigsLoading(loading: boolean): void;
  setError(error: string | null): void;
  setInitialized(initialized: boolean): void;

  // Reset
  reset(): void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const initialState: EventsState = {
  activeTab: 'new',
  me: null,
  members: [],
  selectedAgent: null,
  allAgentsSelected: false,
  eventTypes: [],
  orgEventTypes: [],
  upcomingEvents: [],
  pastEvents: [],
  currentEventInvitees: [],
  expandedEvent: null,
  contact: null,
  searchEmail: '',
  searchPhone: '',
  searchCalendarId: '',
  searchAppointmentTypeId: '',
  searchStatus: '',
  searchFromDate: '',
  searchToDate: '',
  prePopulateConfigs: {},
  loading: false,
  membersLoading: false,
  eventTypesLoading: false,
  eventsLoading: false,
  eventsError: '',
  inviteesLoading: false,
  prePopulateConfigsLoading: false,
  error: null,
  initialized: false,
  lastVisitedEventTypeId: null,
  eventTypesRefreshedAt: null,
};

export const useEventsStore = create<EventsState & EventsActions>((set, get) => ({
  ...initialState,

  // ── Tab navigation ──────────────────────────────────────────────────
  setActiveTab: (tab) => set({ activeTab: tab }),

  // ── Me ──────────────────────────────────────────────────────────────
  setMe: (me) => set({ me }),

  // ── Members ─────────────────────────────────────────────────────────
  setMembers: (members) => set({ members }),

  // ── Agent selection ─────────────────────────────────────────────────
  selectAgent: (agent) =>
    set({
      selectedAgent: agent,
      allAgentsSelected: false,
    }),

  toggleAllAgents: () => {
    const current = get().allAgentsSelected;
    set({
      allAgentsSelected: !current,
      selectedAgent: current ? get().me : null,
    });
  },

  // ── Event types ─────────────────────────────────────────────────────
  setEventTypes: (types) => set({ eventTypes: types }),
  setOrgEventTypes: (types) => set({ orgEventTypes: types }),

  // ── Scheduled events ────────────────────────────────────────────────
  setScheduledEvents: (events) => {
    const now = new Date();
    const upcoming: ScheduledEvent[] = [];
    const past: ScheduledEvent[] = [];

    events.forEach((event) => {
      const eventEnd = new Date(event.endTime || event.startTime);
      if (eventEnd > now) {
        upcoming.push({ ...event, isUpcoming: true });
      } else {
        past.push({ ...event, isUpcoming: false });
      }
    });

    set({ upcomingEvents: upcoming, pastEvents: past });
  },

  clearScheduledEvents: () =>
    set({ upcomingEvents: [], pastEvents: [] }),

  // ── Event detail ────────────────────────────────────────────────────
  setExpandedEvent: (event) => set({ expandedEvent: event }),
  setCurrentEventInvitees: (invitees) =>
    set({ currentEventInvitees: invitees }),

  // ── Contact / search ────────────────────────────────────────────────
  setContact: (contact) => {
    set({
      contact,
      searchEmail: contact?.email || '',
    });
  },

  setSearchEmail: (email) => set({ searchEmail: email }),
  setSearchPhone: (phone) => set({ searchPhone: phone }),
  setSearchCalendarId: (calendarId) => set({ searchCalendarId: calendarId }),
  setSearchAppointmentTypeId: (appointmentTypeId) =>
    set({ searchAppointmentTypeId: appointmentTypeId }),
  setSearchStatus: (status) => set({ searchStatus: status }),
  setSearchDateRange: (fromDate, toDate) =>
    set({ searchFromDate: fromDate, searchToDate: toDate }),

  // ── Last visited ────────────────────────────────────────────────────
  setLastVisitedEventType: (eventTypeId) =>
    set({ lastVisitedEventTypeId: eventTypeId }),

  // ── Pre-populate ────────────────────────────────────────────────────
  setPrePopulateConfig: (agentId, eventTypeId, config) => {
    const existing = get().prePopulateConfigs;
    set({
      prePopulateConfigs: {
        ...existing,
        [agentId]: {
          ...(existing[agentId] || {}),
          [eventTypeId]: config,
        },
      },
    });
  },

  setPrePopulateConfigs: (configs) => set({ prePopulateConfigs: configs }),

  // ── Loading & error ─────────────────────────────────────────────────
  setLoading: (loading) => set({ loading }),
  setMembersLoading: (loading) => set({ membersLoading: loading }),
  setEventTypesLoading: (loading) => set({ eventTypesLoading: loading }),
  setEventTypesRefreshedAt: (time) => set({ eventTypesRefreshedAt: time }),
  setEventsLoading: (loading) => set({ eventsLoading: loading }),
  setEventsError: (error) => set({ eventsError: error }),
  setInviteesLoading: (loading) => set({ inviteesLoading: loading }),
  setPrePopulateConfigsLoading: (loading) => set({ prePopulateConfigsLoading: loading }),
  setError: (error) => set({ error }),
  setInitialized: (initialized) => set({ initialized }),

  // ── Reset ───────────────────────────────────────────────────────────
  reset: () => set(initialState),
}));
